import React, { useState, useCallback } from 'react';
import { dashboardAPI } from '../services/apiClient';
import { appendDateParams } from '../utils/dateParams';

const VoucherSearch = ({ isOpen, onClose, onOpenStoreDrillDown, onOpenValidatorDrillDown, dateRange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMoreResults, setHasMoreResults] = useState(false);

  // Search function that searches both stores and validators
  const searchVouchers = useCallback(async (term) => {
    if (!term.trim()) {
      setResults([]);
      setError(null);
      setHasMoreResults(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Comprehensive search for voucher:', term);
      console.log('Current dateRange:', dateRange);
      
      // Step 1: Get list of available retailers first
      console.log('Step 1: Getting list of available retailers...');
      
      // Build proper query string like StorePerformance does
      const params = new URLSearchParams();
      params.append('Skip', '0');
      params.append('Take', '50'); // Get top 50 retailers
      params.append('SortBy', 'accuracy');
      params.append('SortDirection', 'desc');
      
      // Add date range if provided
      appendDateParams(params, dateRange);
      
      const queryString = `?${params.toString()}`;
      console.log('Using query string:', queryString);
      
      const retailersResponse = await dashboardAPI.getTopRetailersByAccuracy(queryString);
      const retailers = retailersResponse.data?.items || retailersResponse.data || [];
      console.log('Found retailers:', retailers.length);
      console.log('Sample retailers:', retailers.slice(0, 3).map(r => r.retailer || r.name));
      
      if (retailers.length === 0) {
        console.log('❌ No retailers found - API might be unavailable');
        setError('Unable to fetch retailer data');
        return;
      }
      
      // Step 2: Test basic connectivity with actual retailer names
      console.log('Step 2: Testing basic API connectivity with real retailers...');
      const testRetailer = retailers[0].retailer || retailers[0].name;
      console.log('Testing with retailer:', testRetailer);
      
      const basicTestPromise = dashboardAPI.getCouponRedemptions(testRetailer, 0, 10, 'redeemedAt', 'desc', '', false, dateRange);
      const basicTest = await basicTestPromise.catch(e => ({error: e.message}));
      
      console.log('Basic API test result:', basicTest.data || basicTest.error);
      console.log('- Basic test items:', basicTest.data?.items?.length || 0);
      console.log('- Basic test total:', basicTest.data?.total || 0);
      
      if (basicTest.data?.total === 0) {
        console.log('❌ No data available even with specific retailer - checking date range');
        
        // Try without date range to see if that's the issue
        const noDateTest = await dashboardAPI.getCouponRedemptions(testRetailer, 0, 10, 'redeemedAt', 'desc', '', false, null);
        console.log('Test without date range:', noDateTest.data);
        console.log('- No date test total:', noDateTest.data?.total || 0);
        
        if (noDateTest.data?.total === 0) {
          console.log('❌ No data available even without date restrictions');
          setError('No coupon data available in the system');
          return;
        }
      }
      
      // Step 3: Search across top retailers for the voucher
      console.log('Step 3: Searching across multiple retailers...');
      const topRetailers = retailers.slice(0, 5); // Search top 5 retailers
      const combinedResults = [];
      
      for (const retailer of topRetailers) {
        const retailerName = retailer.retailer || retailer.name;
        console.log(`Searching retailer: ${retailerName}`);
        
        try {
          // API appears to be case-insensitive, so just do one search to avoid duplicates
          const searchResult = await dashboardAPI.getCouponRedemptions(
            retailerName, 0, 1000, 'redeemedAt', 'desc', term, false, dateRange
          ).catch(e => ({error: e.message}));
          
          if (searchResult.data?.items?.length > 0) {
            console.log(`Found ${searchResult.data.items.length} matches in ${retailerName}`);
            
            // API already filtered for matches, so let's categorize them
            searchResult.data.items.forEach(item => {
              const code1 = (item.actualCode || '').toLowerCase();
              const code2 = (item.code || '').toLowerCase();
              const searchTerm = term.toLowerCase();
              
              // Check if it's an exact match
              const isExact = code1 === searchTerm || code2 === searchTerm;
              
              console.log(`Processing: ${item.actualCode || item.code} - Exact: ${isExact}`);
              
              combinedResults.push({
                ...item,
                source: 'store',
                sourceDisplay: 'Store',
                location: item.retailer || retailerName,
                searchType: isExact ? `${retailerName}-EXACT` : `${retailerName}-partial`,
                isExactMatch: isExact
              });
            });
            
            const exactCount = searchResult.data.items.filter(item => {
              const code1 = (item.actualCode || '').toLowerCase();
              const code2 = (item.code || '').toLowerCase();
              const searchTerm = term.toLowerCase();
              return code1 === searchTerm || code2 === searchTerm;
            }).length;
            
            console.log(`- Exact matches: ${exactCount}`);
            console.log(`- Partial matches: ${searchResult.data.items.length - exactCount}`);
          }
          
        } catch (error) {
          console.warn(`Error searching retailer ${retailerName}:`, error.message);
        }
      }

      // If no results from API search, try client-side filtering on recent data
      if (combinedResults.length === 0 && basicTest.data?.items?.length > 0) {
        console.log('No API search results, trying client-side filtering...');
        const testRetailer = retailers[0].retailer || retailers[0].name;
        const recentData = await dashboardAPI.getCouponRedemptions(testRetailer, 0, 500, 'redeemedAt', 'desc', '', false, dateRange);
        
        if (recentData.data?.items) {
          console.log(`Got ${recentData.data.items.length} recent items for client-side filtering`);
          
          const filteredItems = recentData.data.items.filter(item => {
            const code1 = (item.actualCode || '').toLowerCase();
            const code2 = (item.code || '').toLowerCase();
            const searchTerm = term.toLowerCase();
            const matches = code1.includes(searchTerm) || code2.includes(searchTerm);
            
            if (matches) {
              console.log(`Client-side match found: ${item.actualCode || item.code}`);
            }
            
            return matches;
          });
          
          console.log(`Client-side filtering found ${filteredItems.length} matches`);
          
          filteredItems.forEach((item) => {
            combinedResults.push({
              ...item,
              source: 'store',
              sourceDisplay: 'Store',
              location: item.retailer,
              searchType: 'client-side'
            });
          });
        }
      }

      // Remove duplicates (in case same result found through different search methods)
      const uniqueResults = [];
      const seenCodes = new Set();
      
      combinedResults.forEach(result => {
        const code = result.actualCode || result.code;
        if (code && !seenCodes.has(code)) {
          seenCodes.add(code);
          uniqueResults.push(result);
        }
      });

      // Sort by date (newest first) and limit to 20
      uniqueResults.sort((a, b) => {
        // First priority: exact matches come first
        if (a.isExactMatch && !b.isExactMatch) return -1;
        if (!a.isExactMatch && b.isExactMatch) return 1;
        
        // Second priority: within same match type, sort by date (newest first)
        return new Date(b.redeemedAt) - new Date(a.redeemedAt);
      });
        
      console.log('FINAL COMPREHENSIVE SEARCH RESULTS:');
      console.log('Total results found:', uniqueResults.length);
      console.log('Store results:', uniqueResults.filter(r => r.source === 'store').length);
      
      const exactMatches = uniqueResults.filter(r => r.isExactMatch);
      const partialMatches = uniqueResults.filter(r => !r.isExactMatch);
      
      console.log('Exact matches:', exactMatches.length);
      console.log('Partial matches:', partialMatches.length);
      
      if (exactMatches.length > 0) {
        console.log('\n🎯 EXACT MATCHES FOUND:');
        exactMatches.forEach((result, index) => {
          console.log(`${index + 1}. 🎯 EXACT: ${result.actualCode || result.code} from ${result.location}`);
        });
        
        if (partialMatches.length > 0) {
          console.log('\n📝 RELATED PARTIAL MATCHES:');
          partialMatches.slice(0, 5).forEach((result, index) => {
            console.log(`${index + 1}. 📝 PARTIAL: ${result.actualCode || result.code} from ${result.location}`);
          });
        }
      } else {
        uniqueResults.forEach((result, index) => {
          const matchType = result.isExactMatch ? '🎯 EXACT' : '📝 PARTIAL';
          console.log(`${index + 1}. ${matchType}: ${result.actualCode || result.code} from ${result.location}`);
        });
      }

      // Show sample of available codes if no matches found 
      if (uniqueResults.length === 0 && basicTest.data?.items?.length > 0) {
        console.log('\n🔍 NO MATCHES FOUND - Here are some available voucher codes for testing:');
        basicTest.data.items.slice(0, 5).forEach((item, index) => {
          console.log(`${index + 1}. Available code: "${item.actualCode || item.code}" from ${item.retailer}`);
        });
      }
        
      const limitedResults = uniqueResults.slice(0, 20);
      const hasMore = uniqueResults.length > 20;

      setResults(limitedResults);
      setHasMoreResults(hasMore);
      
    } catch (err) {
      console.error('Error searching vouchers:', err);
      setError('Failed to search vouchers. Please try again.');
      setResults([]);
      setHasMoreResults(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSearch = () => {
    searchVouchers(searchTerm);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setResults([]);
    setError(null);
    setHasMoreResults(false);
    onClose();
  };

  const openVoucherDetail = (voucher) => {
    if (voucher.source === 'store') {
      // Create store data object for drill-down
      const storeData = {
        name: voucher.location,
        retailer: voucher.retailer,
        // Add other necessary store properties
        id: voucher.retailer // Use retailer as ID
      };
      onOpenStoreDrillDown && onOpenStoreDrillDown(storeData);
    }
    // Note: Validator search unavailable - API endpoints return 500 errors
  };

  const openInvoice = (voucher) => {
    if (voucher.invoiceUri) {
      window.open(voucher.invoiceUri, '_blank');
    }
  };

  const formatCurrency = (value) => `R${(value || 0).toFixed(2)}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString();

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 10003,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '25px',
        width: '800px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
        border: '1px solid #e9ecef'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #e9ecef',
          paddingBottom: '15px'
        }}>
          <h5 style={{ margin: 0, color: '#333', fontSize: '20px', fontWeight: '600' }}>
            <i className="fas fa-search" style={{ marginRight: '10px', color: '#007bff' }}></i>
            Search Vouchers
          </h5>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px',
              borderRadius: '4px',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#333'}
            onMouseOut={(e) => e.target.style.color = '#666'}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Search Input */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter voucher code (e.g. ABC123, DEF456)..."
              style={{
                flex: 1,
                padding: '12px',
                border: '2px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#007bff'}
              onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
            />
            <button
              onClick={handleSearch}
              disabled={!searchTerm.trim() || isLoading}
              style={{
                padding: '12px 20px',
                backgroundColor: searchTerm.trim() && !isLoading ? '#007bff' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: searchTerm.trim() && !isLoading ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Searching...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Search
                </>
              )}
            </button>
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
            Comprehensive search across ALL stores with no date or other restrictions.
            <br />
            <span style={{ color: '#ffc107' }}>Note: Validator search unavailable (API endpoints return errors).</span>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            color: '#721c24',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h6 style={{ margin: 0, color: '#333', fontSize: '16px', fontWeight: '600' }}>
                Search Results ({results.length} found)
              </h6>
              {hasMoreResults && (
                <span style={{
                  fontSize: '12px',
                  color: '#ffc107',
                  backgroundColor: '#fff3cd',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ffeaa7'
                }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                  More results available - refine search for better results
                </span>
              )}
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {results.map((voucher, index) => (
                <div key={index} style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '10px',
                  transition: 'box-shadow 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                onMouseOut={(e) => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 120px 120px',
                    gap: '15px',
                    alignItems: 'center'
                  }}>
                    {/* Voucher Info */}
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '8px'
                      }}>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#333'
                        }}>
                          {voucher.actualCode || voucher.code || 'No Code'}
                        </span>
                        <span style={{
                          fontSize: '11px',
                          backgroundColor: voucher.source === 'store' ? '#007bff' : '#28a745',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontWeight: '500'
                        }}>
                          {voucher.sourceDisplay}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
                        <div><strong>Location:</strong> {voucher.location}</div>
                        <div><strong>Date:</strong> {formatDate(voucher.redeemedAt)}</div>
                        <div><strong>Amount:</strong> {formatCurrency(voucher.discountApplied)}</div>
                        {voucher.campaignName && (
                          <div style={{ marginTop: '4px' }}>
                            <strong>Campaign:</strong> {voucher.campaignName.length > 30 ? 
                              `${voucher.campaignName.substring(0, 30)}...` : voucher.campaignName}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <button
                      onClick={() => openVoucherDetail(voucher)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                      title="Open voucher details"
                    >
                      <i className="fas fa-external-link-alt"></i>
                      Open Voucher
                    </button>

                    <button
                      onClick={() => openInvoice(voucher)}
                      disabled={!voucher.invoiceUri}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: voucher.invoiceUri ? '#28a745' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: voucher.invoiceUri ? 'pointer' : 'not-allowed',
                        fontSize: '12px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                      onMouseOver={(e) => {
                        if (voucher.invoiceUri) e.target.style.backgroundColor = '#1e7e34';
                      }}
                      onMouseOut={(e) => {
                        if (voucher.invoiceUri) e.target.style.backgroundColor = '#28a745';
                      }}
                      title={voucher.invoiceUri ? "View invoice" : "No invoice available"}
                    >
                      <i className="fas fa-file-alt"></i>
                      View Invoice
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {!isLoading && results.length === 0 && searchTerm && !error && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>No vouchers found for "{searchTerm}"</p>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Searched all store data with multiple methods. 
              <br />• Check the browser console (F12) for detailed diagnostic information
              <br />• Try different search terms or partial codes
              <br />• The console shows available voucher codes for testing
              <br />• Voucher might be in validator data (currently unavailable)
            </p>
          </div>
        )}

        {/* Initial State */}
        {!searchTerm && results.length === 0 && !error && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            <i className="fas fa-search" style={{ fontSize: '48px', marginBottom: '16px', color: '#ccc' }}></i>
            <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>Enter a voucher code to search</p>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Comprehensive search across ALL stores without any restrictions (no date limits).
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoucherSearch; 